import {
  Input,
  Button,
  Typography,
  Textarea,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { fetchCountries, fetchCities, addOffice, resetStatus } from "@/store/slices/officeSlice";
import { fetchCountryMobileCode } from "../../store/slices/internalUsersSlice";
import ScrollToTop from "@/components/ScrollToTop";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";
import { useTranslation } from "react-i18next";


const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?]$).*$/;

const getValidationSchema = (t) => Yup.object().shape({
  office_name: Yup.string()
    .trim()
    .required(t("validation.nameRequired"))
    .max(100, t("validation.nameMax"))
    .matches(notBlankRegex, t("validation.nameWhitespace"))
    .matches(noHTMLRegex, t("validation.nameHtml"))
    .matches(noCodeRegex, t("validation.nameScript"))
    .matches(noConsecutiveSpecialChars, t("validation.nameConsecutiveSpecial"))
    .matches(noLeadingTrailingPunct, t("validation.namePunctuation")),

  office_code: Yup.string()
    .trim()
    .required(t("validation.codeRequired"))
    .max(50, t("validation.codeMax"))
    .matches(notBlankRegex, t("validation.codeWhitespace"))
    .matches(noHTMLRegex, t("validation.codeHtml"))
    .matches(noCodeRegex, t("validation.codeScript"))
    .matches(noConsecutiveSpecialChars, t("validation.codeConsecutiveSpecial"))
    .matches(noLeadingTrailingPunct, t("validation.codePunctuation")),

  country_id: Yup.number()
    .nullable()
    .required(t("validation.countryRequired")),

  city_id: Yup.number()
    .nullable()
    .required(t("validation.cityRequired")),

  mobile_country_code: Yup.string()
    .trim()
    .required(t("validation.countryCodeRequired"))
    .max(5, t("validation.countryCodeInvalid"))
    .matches(/^\+?[0-9]{1,5}$/, t("validation.countryCodeDigits")),

  mobile_number: Yup.string()
    .trim()
    .required(t("validation.mobileRequired"))
    .matches(/^[0-9]{7,15}$/, t("validation.mobileDigits")),

  status: Yup.string()
    .oneOf(["active", "inactive"], t("validation.statusInvalid"))
    .required(t("validation.statusRequired")),

  address: Yup.string()
    .trim()
    .required(t("validation.addressRequired"))
    .max(255, t("validation.addressMax"))
    .matches(notBlankRegex, t("validation.addressWhitespace"))
    .matches(noHTMLRegex, t("validation.addressHtml"))
    .matches(noCodeRegex, t("validation.addressScript"))
    .matches(noConsecutiveSpecialChars, t("validation.addressConsecutiveSpecial"))
    .matches(noLeadingTrailingPunct, t("validation.addressPunctuation")),
});

export function AddOffice({ isEdit = false, isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation("office");
  const { t:ct } = useTranslation("common");

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const statusOptions = [{
    label: t('active'),
    value: 'active'
  },
  {
    label: t('inactive'),
    value: 'inactive'
  }];


  const { countries, cities, loading } = useSelector((state) => state.office);
  const { mobile_country_code } = useSelector((state) => state.internalUser);

  const selectedOffice = useSelector((s) => s.office.selected);

  const initialForm = {
    office_name: "",
    office_code: "",
    country_id: null,
    city_id: null,
    mobile_country_code: "",
    mobile_number: "",
    status: "active",
    address: "",
  };

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialForm
  });

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchCountryMobileCode());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit || isView) {
      if (selectedOffice && String(selectedOffice.id) === String(id)) {
        dispatch(fetchCities(selectedOffice.country_id));
        reset({ ...selectedOffice });
      }
    } else {
      reset(initialForm);
    }
  }, [isEdit, isView, id, selectedOffice, dispatch, reset]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addOffice(data)).unwrap();
      if (result.status) {
        Toaster.success(result.message || t("addedSuccess"));
        dispatch(resetStatus());
        reset();
        navigate(`/dashboard/Offices`);
      } else {
        Toaster.error(result.message || t("addFailed"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("addFailedError"));
      dispatch(resetStatus());
    }
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? t('viewTitle') : isEdit ? t('editTitle') : t('addTitle')}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/offices`)}
                  className="rounded-lg"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                  {loading ? t("saving") : isEdit ? t("update") : t("save")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/offices`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("back")}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t("details")}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="space-y-6">
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                    {t("name")} <span className="text-red-500">*</span>
                  </Typography>
                  <Input
                    type="text"
                    size="lg"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    placeholder={t("namePlaceholder")}
                    {...register('office_name')}
                    error={Boolean(errors.office_name)}
                  />
                  {errors.office_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.office_name.message}</p>
                  )}
                </div>
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                    {t("country")} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    placeholder={t("selectCountry")}
                    onChange={(country) => {
                      setValue('country_id', country?.value, { shouldValidate: true });
                      dispatch(fetchCities(country?.value));
                    }}
                    error={Boolean(errors.country_id)}
                    options={countries}
                    value={countries?.find(opt => opt.value === watch('country_id')) || null}
                    isClearable
                    isDisabled={isView}
                  />
                  {errors.country_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.country_id?.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("countryCode")} <span className="text-red-500">*</span>
                    </Typography>
                    <Select
                      type="text"
                      size="lg"
                      className=" !border-t-blue-gray-200 focus:!border-t-gray-900 min-w-[134px]"
                      labelProps={{
                        className: "before:content-none after:content-none",
                      }}
                      placeholder={t("select")}
                      onChange={(code) => {
                        setValue('mobile_country_code', code?.value, { shouldValidate: true });
                      }}
                      value={mobile_country_code?.find(opt => opt.value === watch('mobile_country_code')) || null}
                      options={mobile_country_code}
                      error={Boolean(errors.mobile_country_code)}
                      isClearable
                      isDisabled={isView}
                      components={{
                          Option: CountryOption,
                          SingleValue: CountrySingleValue
                      }}
                    />
                    {errors.mobile_country_code && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.mobile_country_code.message}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("mobile")} <span className="text-red-500">*</span>
                    </Typography>
                    <Input
                      type="text"
                      size="lg"
                      className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                      labelProps={{
                        className: "before:content-none after:content-none",
                      }}
                      placeholder={t("mobilePlaceholder")}
                      {...register("mobile_number")}
                      error={Boolean(errors.mobile_number)}
                    />
                    {errors.mobile_number && (
                      <p className="text-red-500 text-sm mt-1">{errors.mobile_number.message}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                    {t("code")} <span className="text-red-500">*</span>
                  </Typography>
                  <Input
                    type="text"
                    size="lg"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    placeholder={t("codePlaceholder")}
                    {...register('office_code')}
                    error={Boolean(errors.office_code)}
                  />
                  {errors.office_code && (
                    <p className="text-red-500 text-sm mt-1">{errors.office_code.message}</p>
                  )}
                </div>
                <div>
                  <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                    {t("city")} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    placeholder={t("selectCity")}
                    onChange={(city) => {
                      setValue('city_id', city?.value, { shouldValidate: true });
                    }}
                    error={Boolean(errors.city_id)}
                    options={cities}
                    value={cities?.find(opt => opt.value === watch('city_id')) || null}
                    isClearable
                    isDisabled={isView}
                  />
                  {errors.city_id && (
                    <p className="text-red-500 text-sm mt-1">{errors.city_id?.message}</p>
                  )}
                </div>
                <div className="mt-6">
                  <Typography
                    variant="small"
                    color="blue-gray"
                    className="mb-3 font-medium"
                  >
                    {t("status")} <span className="text-red-500">*</span>
                  </Typography>
                  <Select
                    type="text"
                    size="lg"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    placeholder={t("selectStatus")}
                    onChange={(status) => {
                      setValue('status', status?.value, { shouldValidate: true });
                    }}
                    value={statusOptions?.find(opt => opt.value === watch('status')) || null}
                    options={statusOptions}
                    error={Boolean(errors.status)}
                    isClearable
                    isDisabled={isView}
                  />
                  {errors.status && (
                    <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5">
              <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                {t("address")} <span className="text-red-500">*</span>
              </Typography>
              <Textarea
                type="text"
                size="lg"
                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                labelProps={{
                  className: "before:content-none after:content-none",
                }}
                placeholder={t("addressPlaceholder")}
                {...register('address')}
                error={Boolean(errors.address)}
              />
              {errors.address && (
                <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>
              )}
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddOffice;